/**
 * placeSuggest.ts — turn two place sources into one tidy suggestion list for
 * the trip planner's "add a stop" box.
 *
 *  • /api/search (our own Neo4j index) answers instantly as you type: towns
 *    and regions ("Halls Gap", "Grampians") plus campsites with coordinates.
 *  • /api/geocode (Nominatim) fills in national parks, localities and
 *    addresses we don't index. It's rate-limited upstream, so callers only ask
 *    it after a typing pause.
 *
 * Nominatim often returns the same place twice ("Melbourne, Victoria,
 * Australia" for the city and the LGA) — those collapse into one row, and a
 * town we know by name but not by coordinates borrows the geocoder's point.
 *
 * Then everything is ranked like a maps app (rankSuggestions): how well the
 * name answers the query, then what kind of place it is (towns first, regions
 * last), then how close it sits to the trip being built.
 */

import type { SearchCard } from '../types';
import type { GeocodeResult } from './geocodeApi';

/**
 * What a row IS, for ranking and for its icon + type label. Towns lead because
 * a road trip is built from towns; regions trail because "Gippsland" is a
 * place you drive through, not a point you stop at.
 */
export type PlaceType = 'town' | 'park' | 'place' | 'campsite' | 'region';

/**
 * Which glyph + colour a row gets. Finer than PlaceType so a list of eight
 * campsites isn't eight identical tents: a caravan park, a cabin, a bush
 * camp and a rest area each look different at a glance.
 */
export type PlaceIcon = 'town' | 'park' | 'place' | 'region' | 'caravan' | 'cabin' | 'tent' | 'free' | 'rest';

export const PLACE_TYPE_LABEL: Record<PlaceType, string> = {
  town: 'Town',
  park: 'Park',
  place: 'Place',
  campsite: 'Campsite',
  region: 'Region',
};

export interface PlaceSuggestion {
  key: string;
  /** Primary text, e.g. "Halls Gap". */
  label: string;
  /** Secondary text, e.g. "Town · VIC" or "Caravan park · Halls Gap, VIC". */
  detail: string;
  kind: 'place' | 'campsite';
  placeType: PlaceType;
  /** Short type word shown on the row ("Town", "Caravan park"). */
  typeLabel: string;
  /** Glyph + colour for the row (defaults to the placeType's). */
  icon: PlaceIcon;
  /** A photo worth a 40px thumbnail (listings only; never a placeholder). */
  image?: string;
  /** State abbreviation when known — two "Lakes Entrance"s are different places. */
  state?: string | null;
  lat?: number;
  lng?: number;
  /** Campsite entity id (kind === 'campsite'). */
  entityId?: number;
  /** For a place without coordinates: what to geocode on pick. */
  resolveQuery?: string;
}

const AU_STATES: Record<string, string> = {
  'new south wales': 'NSW',
  victoria: 'VIC',
  queensland: 'QLD',
  'south australia': 'SA',
  'western australia': 'WA',
  tasmania: 'TAS',
  'northern territory': 'NT',
  'australian capital territory': 'ACT',
};
const STATE_ABBRS = new Set(Object.values(AU_STATES));

const ENTITY_TYPE_LABEL: Record<string, string> = {
  caravan_park: 'Caravan park',
  campground: 'Campground',
  free_camping: 'Free camp',
  accom: 'Accommodation',
  accommodation: 'Accommodation',
  day_rest_area: 'Rest area',
};

const ENTITY_ICON: Record<string, PlaceIcon> = {
  caravan_park: 'caravan',
  campground: 'tent',
  free_camping: 'free',
  accom: 'cabin',
  accommodation: 'cabin',
  day_rest_area: 'rest',
};

/** A real photo URL, not our "no image" placeholder art. */
function realImage(src: unknown): string | undefined {
  const s = typeof src === 'string' ? src.trim() : '';
  if (!s || /placeholder|default-campsite|no-image/i.test(s)) return undefined;
  if (!/^https?:\/\//.test(s) && !s.startsWith('/')) return undefined;
  return s;
}

const norm = (s: string) =>
  s
    .toLowerCase()
    .replace(/\(.*?\)/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

function stateAbbr(text: string | undefined | null): string | null {
  if (!text) return null;
  const t = text.trim();
  if (STATE_ABBRS.has(t.toUpperCase())) return t.toUpperCase();
  return AU_STATES[t.toLowerCase()] ?? null;
}

const COUNCIL_PREFIX = /^(shire|city|municipality|town|rural city|council|region) of /i;

/** "Halls Gap, Shire of Northern Grampians, Victoria, 3381, Australia"
 *   → { label: "Halls Gap", detail: "VIC", state: "VIC" } */
export function shortPlaceLabel(display: string): { label: string; detail: string; state: string | null } {
  const parts = display.split(',').map((p) => p.trim()).filter(Boolean);
  const label = parts[0] ?? display.trim();
  let state: string | null = null;
  for (const p of parts.slice(1)) {
    state = stateAbbr(p);
    if (state) break;
  }
  const rest = parts
    .slice(1)
    .filter((p) => !/^\d{4}$/.test(p) && !/^australia$/i.test(p) && !stateAbbr(p));
  const area = rest.find((p) => !COUNCIL_PREFIX.test(p));
  const detail = [area, state].filter(Boolean).join(', ') || parts.slice(1).join(', ');
  return { label, detail, state };
}

function km(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/**
 * Town or region? /api/search sends both as type "location". Newer servers say
 * so outright (meta.placeType); older ones don't, but the two branches differ
 * in shape: town rows are grouped from addresses and never carry a point or an
 * image, while regions carry a stored centroid and a hero photo.
 */
function locationPlaceType(c: SearchCard, hasPoint: boolean): PlaceType {
  const declared = String((c.meta as { placeType?: unknown })?.placeType ?? '');
  if (declared === 'town' || declared === 'region') return declared;
  const img = String(c.image ?? '');
  const placeholderImage = !img || /search-placeholder/.test(img);
  return !hasPoint && placeholderImage ? 'town' : 'region';
}

/**
 * Where a listing is, in as few words as do the job: the town when the name
 * doesn't already say it ("Echo Beach Tourist Park" → "Lakes Entrance"), the
 * state when it does ("Lakes Entrance Log Cabins" → "VIC"). "Lakes Entrance,
 * VIC" after a long type word was truncating to "Lakes Entrance,…".
 */
export function campWhere(title: string, subtitle: string): string {
  const parts = subtitle.split(',').map((p) => p.trim()).filter(Boolean);
  const state = parts.map((p) => stateAbbr(p)).find(Boolean) ?? null;
  const town = parts.find((p) => !stateAbbr(p) && !/^\d{4}$/.test(p));
  if (town && !norm(title).includes(norm(town))) return town;
  return state ?? town ?? '';
}

/** Map /api/search cards (location + campsite) to suggestions. */
export function fromSearchCards(cards: SearchCard[]): PlaceSuggestion[] {
  const out: PlaceSuggestion[] = [];
  for (const c of cards) {
    const lat = Number((c.meta as { lat?: unknown })?.lat);
    const lng = Number((c.meta as { lng?: unknown })?.lng);
    const hasPoint = Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0) &&
      (c.meta as { lat?: unknown })?.lat != null;
    if (c.type === 'location') {
      const state = stateAbbr(c.subtitle) ?? c.subtitle;
      const placeType = locationPlaceType(c, hasPoint);
      const typeLabel = PLACE_TYPE_LABEL[placeType];
      out.push({
        key: `loc:${norm(c.title)}:${state ?? ''}`,
        label: c.title,
        detail: state ? `${typeLabel} · ${state}` : typeLabel,
        kind: 'place',
        placeType,
        typeLabel,
        icon: placeType === 'region' ? 'region' : 'town',
        state: stateAbbr(c.subtitle),
        lat: hasPoint ? lat : undefined,
        lng: hasPoint ? lng : undefined,
        resolveQuery: hasPoint ? undefined : [c.title, c.subtitle].filter(Boolean).join(', '),
      });
    } else if (c.type === 'campsite' && hasPoint) {
      const entityType = String((c.meta as { entityType?: unknown })?.entityType ?? '');
      const type = ENTITY_TYPE_LABEL[entityType] ?? 'Campsite';
      const id = Number(c.id);
      out.push({
        key: `camp:${c.id}`,
        label: c.title,
        detail: [type, campWhere(c.title, String(c.subtitle ?? ''))].filter(Boolean).join(' · '),
        kind: 'campsite',
        placeType: 'campsite',
        typeLabel: type,
        icon: ENTITY_ICON[entityType] ?? 'tent',
        image: realImage(c.image),
        state: stateAbbr(String(c.subtitle ?? '').split(',').pop()),
        lat,
        lng,
        entityId: Number.isFinite(id) ? id : undefined,
      });
    }
  }
  return out;
}

const PARK_WORDS = /\b(national park|state park|conservation park|regional park|state forest|nature reserve|reserve)\b/i;

/**
 * Nominatim rows arrive as bare display names. "Lakes Entrance, Victoria,
 * Australia" — a name, then only council/state/postcode/country — is a town;
 * a name with "National Park" in it is a park; anything with a street or
 * suburb trail is just a place.
 */
export function geocodePlaceType(display: string): PlaceType {
  const parts = display.split(',').map((p) => p.trim()).filter(Boolean);
  if (PARK_WORDS.test(parts[0] ?? '')) return 'park';
  const rest = parts
    .slice(1)
    .filter((p) => !/^\d{4}$/.test(p) && !/^australia$/i.test(p) && !stateAbbr(p));
  return rest.every((p) => COUNCIL_PREFIX.test(p)) ? 'town' : 'place';
}

/** Map /api/geocode rows to suggestions. */
export function fromGeocode(rows: GeocodeResult[]): PlaceSuggestion[] {
  return rows
    .filter((r) => finite(r.lat) && finite(r.lng))
    .map((r) => {
      const { label, detail, state } = shortPlaceLabel(r.name);
      const placeType = geocodePlaceType(r.name);
      const typeLabel = PLACE_TYPE_LABEL[placeType];
      return {
        key: `geo:${r.lat.toFixed(3)},${r.lng.toFixed(3)}`,
        label,
        detail: detail ? `${typeLabel} · ${detail}` : typeLabel,
        kind: 'place' as const,
        placeType,
        typeLabel,
        icon: placeType as PlaceIcon,
        state,
        lat: r.lat,
        lng: r.lng,
      };
    });
}

/** Same place? Same name and state, and either close together or one has no point. */
function samePlace(a: PlaceSuggestion, b: PlaceSuggestion): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'campsite') return a.entityId != null && a.entityId === b.entityId;
  if (norm(a.label) !== norm(b.label)) return false;
  // Lakes Entrance VIC is not Lakes Entrance (a Brisbane street) QLD.
  if (a.state && b.state && a.state !== b.state) return false;
  if (finite(a.lat) && finite(a.lng) && finite(b.lat) && finite(b.lng)) {
    // Big places (Grampians, a city and its LGA) geocode to points tens of km apart.
    return km({ lat: a.lat, lng: a.lng }, { lat: b.lat, lng: b.lng }) < 60;
  }
  return true;
}

/** Where the trip already is — the points new suggestions lean towards. */
export type Anchor = { lat: number; lng: number };

/** Results within this distance of the trip get a proximity lift. */
export const NEAR_TRIP_KM = 300;

const TYPE_WEIGHT: Record<PlaceType, number> = {
  town: 6,
  park: 5,
  place: 4,
  campsite: 3,
  region: 0,
};

/** How well a label answers the query: exact 3, prefix 2, every word 1.5, some word 1, else 0. */
export function textMatchScore(label: string, query: string): number {
  const l = norm(label);
  const q = norm(query);
  if (!q) return 0;
  if (l === q) return 3;
  if (l.startsWith(q)) return 2;
  const words = l.split(' ');
  const qWords = q.split(' ');
  if (qWords.every((w) => words.some((lw) => lw.startsWith(w)))) return 1.5;
  if (qWords.some((w) => w.length >= 3 && words.some((lw) => lw.startsWith(w)))) return 1;
  return 0;
}

/** Proximity lift: up to +5 for a result sitting on the trip, fading to 0 at
 *  NEAR_TRIP_KM; a mild −2 for anything across the continent (>1,000 km). */
export function proximityScore(s: Pick<PlaceSuggestion, 'lat' | 'lng'>, anchors: Anchor[]): number {
  if (!anchors.length || !finite(s.lat) || !finite(s.lng)) return 0;
  let best = Infinity;
  for (const a of anchors) {
    if (!finite(a.lat) || !finite(a.lng)) continue;
    best = Math.min(best, km({ lat: s.lat, lng: s.lng }, a));
  }
  if (!Number.isFinite(best)) return 0;
  if (best <= NEAR_TRIP_KM) return 5 * (1 - best / NEAR_TRIP_KM);
  if (best > 1000) return -2;
  return 0;
}

/**
 * Rank like a maps app: does the name answer what was typed (dominant), then
 * what kind of thing it is (town > park > place > campsite > region), then how
 * close it sits to the trip. A region that doesn't even contain the words you
 * typed ("Central Coast of NSW" for "Lakes Entrance") lands at the bottom, and
 * no region outranks a town that matches as well or better.
 */
export function rankSuggestions(
  list: PlaceSuggestion[],
  query: string,
  anchors: Anchor[] = []
): PlaceSuggestion[] {
  const scored = list.map((s, i) => {
    const text = textMatchScore(s.label, query);
    // A region only earns type points when its name starts with what you
    // typed; a fuzzy region hit is noise, not a destination.
    const typeW = s.placeType === 'region' && text < 2 ? -4 : TYPE_WEIGHT[s.placeType];
    return { s, i, score: text * 10 + typeW + proximityScore(s, anchors) };
  });
  scored.sort((a, b) => b.score - a.score || a.i - b.i);
  return scored.map((x) => x.s);
}

/** Beyond this, a result is "across the country" from the trip. */
export const FAR_FROM_TRIP_KM = 500;

function nearestAnchorKm(s: Pick<PlaceSuggestion, 'lat' | 'lng'>, anchors: Anchor[]): number | null {
  if (!finite(s.lat) || !finite(s.lng)) return null;
  let best = Infinity;
  for (const a of anchors) {
    if (!finite(a.lat) || !finite(a.lng)) continue;
    best = Math.min(best, km({ lat: s.lat, lng: s.lng }, a));
  }
  return Number.isFinite(best) ? best : null;
}

/**
 * Drop far-off noise once the trip has a clear answer. If something that
 * matches what you typed sits on the trip (within NEAR_TRIP_KM), rows more
 * than FAR_FROM_TRIP_KM from every stop go: "Lakes Entrance" on a
 * Melbourne → Sydney trip shouldn't offer a street in Springfield Lakes, QLD.
 * A same-named TOWN elsewhere stays (there are three Richmonds), just lower.
 * Rows without a point, or trips without anchors, are left alone.
 */
export function pruneFarFromTrip(
  list: PlaceSuggestion[],
  query: string,
  anchors: Anchor[] = []
): PlaceSuggestion[] {
  if (!anchors.length || !query.trim()) return list;
  const nearHit = list.some((s) => {
    const d = nearestAnchorKm(s, anchors);
    return d !== null && d <= NEAR_TRIP_KM && textMatchScore(s.label, query) >= 2;
  });
  if (!nearHit) return list;
  return list.filter((s) => {
    const d = nearestAnchorKm(s, anchors);
    if (d === null || d <= FAR_FROM_TRIP_KM) return true;
    return s.placeType === 'town' && textMatchScore(s.label, query) === 3;
  });
}

/**
 * A region is only worth a row when its name answers what was typed.
 * "Central Coast of NSW" for "Lakes Entrance" is a search-engine echo (it
 * matched on a description somewhere), not a place you meant, so it goes.
 * "Gipps" still offers Gippsland.
 */
export function dropIrrelevantRegions(list: PlaceSuggestion[], query: string): PlaceSuggestion[] {
  if (norm(query).length < 3) return list;
  return list.filter((s) => s.placeType !== 'region' || textMatchScore(s.label, query) >= 1.5);
}

/**
 * Merge + dedupe, then rank (rankSuggestions). A place without coordinates
 * borrows them from a matching geocoder row, so a town we know only by name
 * still gets its proximity lift.
 */
export function mergeSuggestions(
  search: PlaceSuggestion[],
  geocode: PlaceSuggestion[],
  limit = 8,
  query = '',
  anchors: Anchor[] = []
): PlaceSuggestion[] {
  const places: PlaceSuggestion[] = [];
  const camps: PlaceSuggestion[] = [];
  const add = (s: PlaceSuggestion) => {
    const bucket = s.kind === 'campsite' ? camps : places;
    const dupe = bucket.find((x) => samePlace(x, s));
    if (dupe) {
      if (!finite(dupe.lat) && finite(s.lat) && finite(s.lng)) {
        dupe.lat = s.lat;
        dupe.lng = s.lng;
        dupe.resolveQuery = undefined;
      }
      if (!dupe.state && s.state) dupe.state = s.state;
      return;
    }
    bucket.push({ ...s });
  };
  search.filter((s) => s.kind === 'place').forEach(add);
  geocode.forEach(add);
  search.filter((s) => s.kind === 'campsite').forEach(add);
  if (query.trim()) {
    const pruned = pruneFarFromTrip(dropIrrelevantRegions([...places, ...camps], query), query, anchors);
    return rankSuggestions(pruned, query, anchors).slice(0, limit);
  }
  // No query to rank against: places first, then campsites. Up to 3
  // campsites keep a spot even when there are lots of places.
  const takePlaces = places.slice(0, limit - Math.min(camps.length, 3));
  return [...takePlaces, ...camps.slice(0, limit - takePlaces.length)];
}
